/**
 * O que cada capacidade de IA disse, numa frase.
 *
 * Mora fora das telas porque duas o usam: o Processamento de imagens (a miniatura do lote)
 * e a Operação (o painel de APIs de um trânsito que nasceu de upload). Duas cópias desta
 * função seriam duas maneiras de dizer "placa ilegível".
 */
import type { ResultadoDaCapacidade } from "./api/tipos";

/**
 * Passeio ou comercial — as duas classes que a operação decide (ADR-028). Passeio é carro e
 * moto; comercial é todo o resto. Aceita a classe do contrato de visão (`caminhao`), o rótulo
 * do seed (`caminhão`, `automóvel`) e o próprio grupo, para toda tela dizer a mesma coisa.
 */
const GRUPO: Record<string, "Passeio" | "Comercial"> = {
  passeio: "Passeio",
  moto: "Passeio",
  motocicleta: "Passeio",
  automovel: "Passeio",
  automóvel: "Passeio",
  carro: "Passeio",
  comercial: "Comercial",
  utilitario: "Comercial",
  utilitário: "Comercial",
  caminhao: "Comercial",
  caminhão: "Comercial",
  onibus: "Comercial",
  ônibus: "Comercial",
  van: "Comercial",
};

export function grupoDaClasse(
  classe: string | null | undefined,
): "Passeio" | "Comercial" | null {
  if (!classe) return null;
  return GRUPO[classe.trim().toLowerCase()] ?? null;
}

export function resumoDaCapacidade(r: ResultadoDaCapacidade): {
  texto: string;
  falhou: boolean;
} {
  if (r.desfecho === "INDISPONIVEL")
    return { texto: "falhou aqui", falhou: true };
  if (r.desfecho === "ENTRADA_INADEQUADA")
    return { texto: "imagem inadequada", falhou: true };

  const valor = (r.valor ?? {}) as Record<string, unknown>;
  if (r.capacidade === "ocr_placa") {
    const placa = valor.placa as string | null | undefined;
    // "placa ilegível" é o modelo tendo olhado e não lido — informação, não falha.
    return { texto: placa ?? "placa ilegível", falhou: !placa };
  }
  if (r.capacidade === "classificacao_veiculo") {
    return {
      texto:
        grupoDaClasse(
          (valor.grupo as string | undefined) ??
            (valor.classe as string | undefined),
        )?.toLowerCase() ?? "—",
      falhou: false,
    };
  }
  if (r.capacidade === "contagem_eixos") {
    // Três coisas diferentes, e nenhuma delas é "0 eixos": moto não tem eixos a contar,
    // ângulo ruim impede a contagem, e um número é um número.
    if (valor.aplicavel === false)
      return { texto: "eixos não se aplicam", falhou: false };
    if (valor.eixos == null)
      return { texto: "não deu para contar", falhou: false };
    return { texto: `${valor.eixos as number} eixos`, falhou: false };
  }
  return { texto: r.desfecho.toLowerCase(), falhou: false };
}
